import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useAuth} from '@cap4/auth';
import {Modal} from '@cap4/ui';
import {supabase} from '../lib/api';
import {isNewer, releases, versionLabel} from './history';
import './releases.css';

const labels = {new: 'Nyt', improved: 'Forbedret', fixed: 'Rettet'};
const sessionSeen = new Map<string, string>();
const storageKey = (id: string) => `cap4publish:seen-release:${id}`;
function localSeen(id: string) {
  try { return localStorage.getItem(storageKey(id)) || localStorage.getItem(`publisher:seen-release:${id}`) || sessionSeen.get(id); }
  catch { return sessionSeen.get(id); }
}

export function ReleaseNotes() {
  const {user} = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileSlot, setMobileSlot] = useState<HTMLElement | null>(null);
  const latest = releases[0];
  const userId = user?.id;
  useEffect(() => { setMobileSlot(document.getElementById('cap4publish-mobile-release')); }, []);

  useEffect(() => {
    if (!userId || !latest || !supabase) return;
    let cancelled = false;
    // Read account state afresh so seeing a release on another device counts.
    void supabase.auth.getUser().then(({data, error}) => {
      if (cancelled || error || data.user?.id !== userId) return;
      const remoteSeen = data.user.user_metadata.cap4publish_seen_release ?? data.user.user_metadata.publisher_seen_release;
      const seenHere = localSeen(userId);
      if (isNewer(latest.version, remoteSeen) && isNewer(latest.version, seenHere)) setOpen(true);
      // Retry a failed account write from a previous visit, without reopening.
      if (isNewer(latest.version, remoteSeen) && seenHere === latest.version) {
        void supabase!.auth.updateUser({data: {cap4publish_seen_release: latest.version}}).catch(() => {});
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, latest]);

  useEffect(() => {
    if (!open || !latest || !userId || !supabase) return;
    const seen = localSeen(userId);
    if (isNewer(latest.version, seen)) {
      sessionSeen.set(userId, latest.version);
      try { localStorage.setItem(storageKey(userId), latest.version); } catch { /* Session fallback. */ }
    }
    // Check the current account version before recording this view.
    void supabase.auth.getUser().then(({data, error}) => {
      if (!error && data.user?.id === userId && isNewer(latest.version, data.user.user_metadata.cap4publish_seen_release ?? data.user.user_metadata.publisher_seen_release)) {
        return supabase!.auth.updateUser({data: {cap4publish_seen_release: latest.version}});
      }
    }).catch(() => {});
  }, [open, latest, userId]);

  const trigger = (mobile: boolean) => <button className={`release-trigger ${mobile ? 'release-trigger-mobile' : 'release-trigger-desktop'}`} onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label="Åbn versionshistorik">
      {latest ? versionLabel(latest.version) : 'Beta'}
    </button>;
  return <>
    {trigger(false)}
    {mobileSlot && createPortal(trigger(true), mobileSlot)}
    {open && <Modal title="Nyt i CAP4Publish" onClose={() => setOpen(false)} cancelLabel="Luk" className="release-modal">
      <p className="release-intro">Se, hvad der er kommet til, og hvad vi har forbedret.</p>
      {releases.length === 0 && <p>Her finder du versionshistorikken fra den første betaudgivelse.</p>}
      {releases.map((release, index) => <article className="release-entry" key={release.version}>
        <div className="release-meta"><strong>{versionLabel(release.version)}</strong>{index === 0 && <span>Seneste version</span>}
          <time dateTime={release.date}>{new Intl.DateTimeFormat('da-DK', {day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Copenhagen'}).format(new Date(release.date))}</time>
        </div>
        <h3>{release.title}</h3>
        {(['new', 'improved', 'fixed'] as const).map(kind => {
          const changes = release.changes.filter(change => change.kind === kind);
          return changes.length > 0 && <section key={kind}><h4>{labels[kind]}</h4><ul>{changes.map((change, i) => <li key={i}>{change.text}</li>)}</ul></section>;
        })}
      </article>)}
    </Modal>}
  </>;
}
